/**
 * Vaccine Schedule Generator
 * 
 * Generates HSE (Health Service Executive) childhood immunisation schedules
 * based on patient date of birth.
 * 
 * Reference: https://www.hse.ie/eng/health/immunisation/pubinfo/currentschedule.html
 */

import { ScheduledVaccine, VaccineSchedule, Immunisation } from '../types/immunisation'

// HSE Schedule cutoff date: October 1, 2024
const SCHEDULE_CUTOFF_DATE = new Date('2024-10-01')

/**
 * Calculate due date based on date of birth and age in months
 */
export function calculateDueDate(dateOfBirth: string, ageInMonths: number): Date {
  const birthDate = new Date(dateOfBirth)
  const dueDate = new Date(birthDate)
  dueDate.setMonth(birthDate.getMonth() + ageInMonths)
  return dueDate
}

/**
 * Determine which schedule version to use based on date of birth
 */
export function getScheduleVersion(dateOfBirth: string): 'post-oct-2024' | 'pre-oct-2024' {
  const dob = new Date(dateOfBirth)
  return dob >= SCHEDULE_CUTOFF_DATE ? 'post-oct-2024' : 'pre-oct-2024'
}

/**
 * Generate HSE schedule for babies born on or after October 1, 2024
 */
function generatePostOct2024Schedule(dateOfBirth: string, patientId: string): ScheduledVaccine[] {
  const schedule: ScheduledVaccine[] = []

  // 2 Months: 6 in 1 + MenB + PCV + Rotavirus (3 injections + oral)
  schedule.push({
    scheduleId: `sched-${patientId}-2m`,
    patientId,
    age: '2 months',
    ageInMonths: 2,
    vaccineTypes: ['6 in 1', 'MenB', 'PCV', 'Rotavirus'],
    dueDate: calculateDueDate(dateOfBirth, 2).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 3,
    status: 'due',
  })

  // 4 Months: 6 in 1 + MenB + Rotavirus (2 injections + oral)
  schedule.push({
    scheduleId: `sched-${patientId}-4m`,
    patientId,
    age: '4 months',
    ageInMonths: 4,
    vaccineTypes: ['6 in 1', 'MenB', 'Rotavirus'],
    dueDate: calculateDueDate(dateOfBirth, 4).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 2,
    status: 'due',
  })

  // 6 Months: 6 in 1 + PCV (2 injections)
  schedule.push({
    scheduleId: `sched-${patientId}-6m`,
    patientId,
    age: '6 months',
    ageInMonths: 6,
    vaccineTypes: ['6 in 1', 'PCV'],
    dueDate: calculateDueDate(dateOfBirth, 6).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 2,
    status: 'due',
  })

  // 12 Months: MMR + MenB + Chickenpox (3 injections)
  schedule.push({
    scheduleId: `sched-${patientId}-12m`,
    patientId,
    age: '12 months',
    ageInMonths: 12,
    vaccineTypes: ['MMR', 'MenB', 'Chickenpox'],
    dueDate: calculateDueDate(dateOfBirth, 12).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 3,
    status: 'due',
  })

  // 13 Months: 6 in 1 + MenC + PCV (3 injections)
  schedule.push({
    scheduleId: `sched-${patientId}-13m`,
    patientId,
    age: '13 months',
    ageInMonths: 13,
    vaccineTypes: ['6 in 1', 'MenC', 'PCV'],
    dueDate: calculateDueDate(dateOfBirth, 13).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 3,
    status: 'due',
  })

  // Junior Infants: 4 in 1 + MMR (2 injections, school)
  // Note: This is typically around age 4-5 years, but we'll calculate based on school entry age
  // For simplicity, we'll use 4 years (48 months) as the age
  schedule.push({
    scheduleId: `sched-${patientId}-ji`,
    patientId,
    age: 'Junior Infants',
    ageInMonths: 48, // Approximately 4 years
    vaccineTypes: ['4 in 1', 'MMR'],
    dueDate: calculateDueDate(dateOfBirth, 48).toISOString().split('T')[0],
    location: 'School',
    numberOfInjections: 2,
    status: 'due',
  })

  // 1st Year (Secondary): HPV9 + MenACWY + Tdap (3 injections, school)
  // Note: This is typically around age 12-13 years (144-156 months)
  // We'll use 12 years (144 months) as the age
  schedule.push({
    scheduleId: `sched-${patientId}-1st-year`,
    patientId,
    age: '1st Year',
    ageInMonths: 144, // Approximately 12 years
    vaccineTypes: ['HPV9', 'MenACWY', 'Tdap'],
    dueDate: calculateDueDate(dateOfBirth, 144).toISOString().split('T')[0],
    location: 'School',
    numberOfInjections: 3,
    status: 'due',
  })

  return schedule
}

/**
 * Generate HSE schedule for babies born before October 1, 2024
 * Note: This uses the previous schedule format (details to be confirmed from HSE documentation)
 * For now, we'll use a similar structure but may need adjustment based on actual pre-Oct 2024 schedule
 */
function generatePreOct2024Schedule(dateOfBirth: string, patientId: string): ScheduledVaccine[] {
  // The pre-Oct 2024 schedule was similar but may have had different timing or vaccines
  // For now, we'll use the same structure but this may need to be updated with actual historical schedule
  // Reference: Previous HSE schedule documentation needed
  
  const schedule: ScheduledVaccine[] = []

  // 2 Months: 6 in 1 + MenB + PCV (3 injections) - Rotavirus may not have been included
  schedule.push({
    scheduleId: `sched-${patientId}-2m`,
    patientId,
    age: '2 months',
    ageInMonths: 2,
    vaccineTypes: ['6 in 1', 'MenB', 'PCV'],
    dueDate: calculateDueDate(dateOfBirth, 2).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 3,
    status: 'due',
  })

  // 4 Months: 6 in 1 + MenB (2 injections)
  schedule.push({
    scheduleId: `sched-${patientId}-4m`,
    patientId,
    age: '4 months',
    ageInMonths: 4,
    vaccineTypes: ['6 in 1', 'MenB'],
    dueDate: calculateDueDate(dateOfBirth, 4).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 2,
    status: 'due',
  })

  // 6 Months: 6 in 1 + PCV (2 injections)
  schedule.push({
    scheduleId: `sched-${patientId}-6m`,
    patientId,
    age: '6 months',
    ageInMonths: 6,
    vaccineTypes: ['6 in 1', 'PCV'],
    dueDate: calculateDueDate(dateOfBirth, 6).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 2,
    status: 'due',
  })

  // 12 Months: MMR + MenB (2 injections) - Chickenpox may not have been included
  schedule.push({
    scheduleId: `sched-${patientId}-12m`,
    patientId,
    age: '12 months',
    ageInMonths: 12,
    vaccineTypes: ['MMR', 'MenB'],
    dueDate: calculateDueDate(dateOfBirth, 12).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 2,
    status: 'due',
  })

  // 13 Months: 6 in 1 + MenC + PCV (3 injections)
  schedule.push({
    scheduleId: `sched-${patientId}-13m`,
    patientId,
    age: '13 months',
    ageInMonths: 13,
    vaccineTypes: ['6 in 1', 'MenC', 'PCV'],
    dueDate: calculateDueDate(dateOfBirth, 13).toISOString().split('T')[0],
    location: 'GP',
    numberOfInjections: 3,
    status: 'due',
  })

  // Junior Infants: 4 in 1 + MMR (2 injections, school)
  schedule.push({
    scheduleId: `sched-${patientId}-ji`,
    patientId,
    age: 'Junior Infants',
    ageInMonths: 48,
    vaccineTypes: ['4 in 1', 'MMR'],
    dueDate: calculateDueDate(dateOfBirth, 48).toISOString().split('T')[0],
    location: 'School',
    numberOfInjections: 2,
    status: 'due',
  })

  // 1st Year (Secondary): HPV9 + MenACWY + Tdap (3 injections, school)
  schedule.push({
    scheduleId: `sched-${patientId}-1st-year`,
    patientId,
    age: '1st Year',
    ageInMonths: 144,
    vaccineTypes: ['HPV9', 'MenACWY', 'Tdap'],
    dueDate: calculateDueDate(dateOfBirth, 144).toISOString().split('T')[0],
    location: 'School',
    numberOfInjections: 3,
    status: 'due',
  })

  return schedule
}

/**
 * Get schedule status based on due date and recorded immunisations
 * Returns updated ScheduledVaccine with status and related immunisation IDs
 */
export function getScheduleStatus(
  scheduledVaccine: ScheduledVaccine,
  immunisations: Immunisation[],
): ScheduledVaccine {
  const today = new Date()
  const dueDate = new Date(scheduledVaccine.dueDate)
  
  // Check if any immunisation matches this scheduled vaccine
  const matchingImmunisations = immunisations.filter((imm) => {
    // Check if immunisation type matches any of the scheduled vaccine types
    return scheduledVaccine.vaccineTypes.some((vaccineType) =>
      imm.type.toLowerCase().includes(vaccineType.toLowerCase()) ||
      vaccineType.toLowerCase().includes(imm.type.toLowerCase())
    )
  })

  if (matchingImmunisations.length > 0) {
    // Find the earliest matching immunisation date
    const earliestDate = matchingImmunisations
      .map((imm) => new Date(imm.date))
      .sort((a, b) => a.getTime() - b.getTime())[0]
    
    return {
      ...scheduledVaccine,
      status: 'given',
      givenDate: earliestDate.toISOString().split('T')[0],
      immunisationIds: matchingImmunisations.map((imm) => imm.immunisationId),
    }
  }

  // Check if overdue
  if (dueDate < today) {
    return {
      ...scheduledVaccine,
      status: 'overdue',
    }
  }

  // Still due
  return {
    ...scheduledVaccine,
    status: 'due',
  }
}

/**
 * Match an immunisation to a scheduled vaccine
 */
export function matchImmunisationToSchedule(
  immunisation: Immunisation,
  schedule: ScheduledVaccine[],
): ScheduledVaccine | null {
  // Try to match by scheduledVaccineId first
  if (immunisation.scheduledVaccineId) {
    const matched = schedule.find((sv) => sv.scheduleId === immunisation.scheduledVaccineId)
    if (matched) return matched
  }

  // Try to match by vaccine type and date proximity
  for (const scheduledVaccine of schedule) {
    const isTypeMatch = scheduledVaccine.vaccineTypes.some((vaccineType) =>
      immunisation.type.toLowerCase().includes(vaccineType.toLowerCase()) ||
      vaccineType.toLowerCase().includes(immunisation.type.toLowerCase())
    )

    if (isTypeMatch) {
      const immunisationDate = new Date(immunisation.date)
      const dueDate = new Date(scheduledVaccine.dueDate)
      const daysDifference = Math.abs(
        (immunisationDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
      )

      // Consider it a match if within 90 days of due date
      if (daysDifference <= 90) {
        return scheduledVaccine
      }
    }
  }

  return null
}

/**
 * Generate HSE schedule based on patient date of birth
 */
export function generateHseSchedule(dateOfBirth: string, patientId: string): ScheduledVaccine[] {
  const scheduleVersion = getScheduleVersion(dateOfBirth)
  
  if (scheduleVersion === 'post-oct-2024') {
    return generatePostOct2024Schedule(dateOfBirth, patientId)
  } else {
    return generatePreOct2024Schedule(dateOfBirth, patientId)
  }
}

/**
 * Generate complete vaccine schedule with status based on recorded immunisations
 */
export function generateVaccineSchedule(
  dateOfBirth: string,
  patientId: string,
  immunisations: Immunisation[] = [],
): VaccineSchedule {
  const scheduleVersion = getScheduleVersion(dateOfBirth)
  const scheduledVaccines = generateHseSchedule(dateOfBirth, patientId)
  
  // Update status for each scheduled vaccine based on recorded immunisations
  const scheduledVaccinesWithStatus = scheduledVaccines.map((sv) =>
    getScheduleStatus(sv, immunisations)
  )

  return {
    patientId,
    dateOfBirth,
    scheduleVersion,
    scheduledVaccines: scheduledVaccinesWithStatus,
  }
}
